import { EmittedEvents } from '@squareup/cdp';
import { OneTrustConsent, hasConsent } from '@squareup/service-one-trust';
import { readString, writeString } from '@squareup/util-local-storage';
const EVENT_CAPTURE_QUERY_PARAM = 'eventCapture';
const EVENT_CAPTURE_LOCAL_STORAGE_KEY = 'dashboard-event-capture-enabled';
const GLOBAL_STATE_KEY = '__eventCaptureState';
const GLOBAL_API_KEY = '__eventCaptureApi';
let globalState = null;
let captureEnabled = false;
const IS_BROWSER = typeof window !== 'undefined';
function isOptInQueryParamPresent() {
    const { search } = window.location;
    if (!search) {
        return false;
    }
    const params = new URLSearchParams(search);
    return params.get(EVENT_CAPTURE_QUERY_PARAM) !== null;
}
function isEventCapturingRequested() {
    if (isOptInQueryParamPresent()) {
        return true;
    }
    const localStorageValue = readString(EVENT_CAPTURE_LOCAL_STORAGE_KEY, OneTrustConsent.PERFORMANCE);
    return localStorageValue.success && localStorageValue.value === 'true';
}
function isCdpInvokeEvent(event) {
    return event instanceof CustomEvent;
}
function handleCdpInvoke(event) {
    if (!globalState || !captureEnabled || !isCdpInvokeEvent(event)) {
        return;
    }
    globalState.events.push({
        source: 'cdp',
        payload: event.detail,
    });
}
function ensureCdpListener() {
    if (!globalState || globalState.cdpListenerAttached) {
        return;
    }
    window.addEventListener(EmittedEvents.Invoke, handleCdpInvoke);
    globalState.cdpListenerAttached = true;
}
function getGlobalState() {
    if (!IS_BROWSER) {
        return null;
    }
    if (globalState) {
        return globalState;
    }
    if (window[GLOBAL_STATE_KEY]) {
        globalState = window[GLOBAL_STATE_KEY];
        return globalState;
    }
    return null;
}
function createGlobalState() {
    window[GLOBAL_STATE_KEY] = {
        events: [],
        metadata: null,
        cdpListenerAttached: false,
    };
    globalState = window[GLOBAL_STATE_KEY];
    return globalState;
}
function exposeCaptureApiToWindow() {
    if (window[GLOBAL_API_KEY]) {
        return;
    }
    window[GLOBAL_API_KEY] = {
        download: downloadCapturedEvents,
        clear: clearCapturedEvents,
    };
}
export function initializeEventCapturing(options) {
    if (!IS_BROWSER || !hasConsent(OneTrustConsent.PERFORMANCE)) {
        return false;
    }
    if (!isEventCapturingRequested()) {
        return false;
    }
    writeString(EVENT_CAPTURE_LOCAL_STORAGE_KEY, 'true', OneTrustConsent.PERFORMANCE);
    captureEnabled = true;
    // Ember and React will both call initializeEventCapturing, so we need to ensure
    // we only create the global state once
    const state = getGlobalState() ?? createGlobalState();
    if (!state.metadata) {
        state.metadata = {
            bodyId: document.body?.id || '',
            createdAt: new Date().toISOString(),
            environment: options.environment,
            initializingFramework: options.initializingFramework,
            shell: options.shell,
            ...(options.userContext ? { userContext: options.userContext } : {}),
        };
    }
    ensureCdpListener();
    exposeCaptureApiToWindow();
    return true;
}
export function recordEs2Event(payload) {
    const state = getGlobalState();
    if (!state || !captureEnabled) {
        return;
    }
    state.events.push({
        source: 'es2',
        payload,
    });
}
function clearCapturedEvents() {
    const state = getGlobalState();
    if (!state) {
        return;
    }
    state.events.length = 0;
}
function formatDate() {
    return new Date().toISOString().split('T')[0];
}
function downloadCapturedEvents(filenamePrefix = 'event-capture') {
    const state = getGlobalState();
    if (!state || !state.metadata) {
        return;
    }
    const payload = {
        metadata: state.metadata,
        events: state.events,
    };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${filenamePrefix}-${formatDate()}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}
